import * as React from "react";
import { createBrowserRouter, Navigate, useSearchParams } from "react-router";
import { AuthGate } from "@/components/layout/AuthGate";
import { RoleGate } from "@/components/layout/RoleGate";
import { PLATFORM_SURFACE_REASONS } from "@/components/layout/adminSurfaces";
import { MobileGuard } from "@/platform/MobileGuard";
import { useMe } from "@/api/me";

// ─── Layout shells (small — eager-load is fine) ───────────────────────────────
import { AppShell } from "@/components/layout/AppShell";
import { AdminShell } from "@/components/layout/AdminShell";
import { AdminPlatformShell } from "@/components/layout/AdminPlatformShell";
import { MovedRoute } from "@/components/layout/MovedRoute";
import { AdminIndexRedirect } from "@/components/layout/AdminIndexRedirect";
import {
  SettingsIndex,
  SettingsShell,
} from "@/components/layout/SettingsShell";
import { SiteOwnerRedirect } from "@/components/layout/SiteOwnerRedirect";
import { DashboardRouter } from "@/components/layout/DashboardRouter";
import { ChunkErrorElement } from "@/components/layout/ChunkErrorElement";

// ─── Lazy page components ─────────────────────────────────────────────────────
const InboxPage = React.lazy(() =>
  import("@/pages/InboxPage").then((m) => ({ default: m.InboxPage })),
);
const NotificationsPage = React.lazy(() =>
  import("@/pages/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const MapViewPage = React.lazy(() =>
  import("@/pages/MapViewPage").then((m) => ({ default: m.MapViewPage })),
);
const SitesListPage = React.lazy(() =>
  import("@/pages/SitesListPage").then((m) => ({ default: m.SitesListPage })),
);
const SiteDetailShell = React.lazy(() =>
  import("@/components/site/SiteDetailShell").then((m) => ({
    default: m.SiteDetailShell,
  })),
);
const SiteOverviewTab = React.lazy(() =>
  import("@/pages/site/SiteOverviewTab").then((m) => ({
    default: m.SiteOverviewTab,
  })),
);
// MV2-1154 — the energy shell's new tabs (src/domains/core/site).
const SiteAssetsTab = React.lazy(() =>
  import("@/domains/core/site/SiteAssetsTab").then((m) => ({ default: m.SiteAssetsTab })),
);
const SiteAssetPage = React.lazy(() =>
  import("@/domains/core/site/assets/SiteAssetPage").then((m) => ({ default: m.SiteAssetPage })),
);
const SiteCarbonTab = React.lazy(() =>
  import("@/domains/core/site/SiteCarbonTab").then((m) => ({ default: m.SiteCarbonTab })),
);
const SiteAnalyticsTab = React.lazy(() =>
  import("@/pages/site/SiteAnalyticsTab").then((m) => ({
    default: m.SiteAnalyticsTab,
  })),
);
const SiteReportsTab = React.lazy(() =>
  import("@/pages/site/SiteReportsTab").then((m) => ({
    default: m.SiteReportsTab,
  })),
);
const SiteAlarmsTab = React.lazy(() =>
  import("@/pages/site/SiteAlarmsTab").then((m) => ({
    default: m.SiteAlarmsTab,
  })),
);
const SiteTicketsTab = React.lazy(() =>
  import("@/pages/site/SiteTicketsTab").then((m) => ({
    default: m.SiteTicketsTab,
  })),
);
const SiteDevicesTab = React.lazy(() =>
  import("@/pages/site/SiteDevicesTab").then((m) => ({
    default: m.SiteDevicesTab,
  })),
);
const SiteGridCodeTab = React.lazy(() =>
  import("@/pages/site/SiteGridCodeTab").then((m) => ({
    default: m.SiteGridCodeTab,
  })),
);
const SiteStringsTab = React.lazy(() =>
  import("@/pages/site/SiteStringsTab").then((m) => ({
    default: m.SiteStringsTab,
  })),
);
const SiteControlsTab = React.lazy(() =>
  import("@/pages/site/SiteControlsTab").then((m) => ({
    default: m.SiteControlsTab,
  })),
);
const SiteControlDevicePage = React.lazy(
  () => import("@/pages/site/SiteControlDevicePage"),
);
const SiteDevicePage = React.lazy(() =>
  import("@/pages/site/SiteDevicePage").then((m) => ({
    default: m.SiteDevicePage,
  })),
);
const SiteBESSEditor = React.lazy(() =>
  import("@/pages/site/SiteBESSEditor").then((m) => ({
    default: m.SiteBESSEditor,
  })),
);
// MV2-692 — Site Simulator. Standalone client-level surface (existing-site
// and greenfield modes + saved scenarios), replacing the MV2-630 per-site
// tab. Client-level so greenfield/prospect scenarios with no site row have a
// home; gated in the router below on role + the `view_simulator` policy
// feature (defence-in-depth on top of the Sidebar gate).
const SimulatorPage = React.lazy(() =>
  import("@/pages/simulator/SimulatorPage").then((m) => ({
    default: m.SimulatorPage,
  })),
);
const ScenarioEditor = React.lazy(() =>
  import("@/pages/simulator/ScenarioEditor").then((m) => ({
    default: m.ScenarioEditor,
  })),
);
// MV2-1100 — the PVsyst import is its own screen, reached from the New
// scenario menu the same way Greenfield is: the menu navigates rather than
// opening a panel on the scenario library.
const PvsystImportPage = React.lazy(() =>
  import("@/pages/simulator/PvsystImportPage").then((m) => ({
    default: m.PvsystImportPage,
  })),
);
const SiteTariffTab = React.lazy(() =>
  import("@/pages/site/SiteTariffTab").then((m) => ({
    default: m.SiteTariffTab,
  })),
);
const SiteFinancialsTab = React.lazy(() =>
  import("@/pages/site/SiteFinancialsTab").then((m) => ({
    default: m.SiteFinancialsTab,
  })),
);
const SiteSettingsTab = React.lazy(() =>
  import("@/pages/site/SiteSettingsTab").then((m) => ({
    default: m.SiteSettingsTab,
  })),
);
const SiteFilesTab = React.lazy(() =>
  import("@/pages/site/SiteFilesTab").then((m) => ({
    default: m.SiteFilesTab,
  })),
);
const SiteRawTab = React.lazy(() =>
  import("@/pages/site/SiteRawTab").then((m) => ({
    default: m.SiteRawTab,
  })),
);
const SiteConnectivityTab = React.lazy(() =>
  import("@/pages/site/SiteConnectivityTab").then((m) => ({
    default: m.SiteConnectivityTab,
  })),
);
const MyPlantPage = React.lazy(() =>
  import("@/pages/MyPlantPage").then((m) => ({ default: m.MyPlantPage })),
);
const MyPlantsGridPage = React.lazy(() =>
  import("@/pages/MyPlantsGridPage").then((m) => ({
    default: m.MyPlantsGridPage,
  })),
);
const MyPlantRedirect = React.lazy(() =>
  import("@/pages/MyPlantsGridPage").then((m) => ({
    default: m.MyPlantRedirect,
  })),
);
const AlarmsListPage = React.lazy(() =>
  import("@/pages/AlarmsListPage").then((m) => ({ default: m.AlarmsListPage })),
);
const AlarmDetailPage = React.lazy(() =>
  import("@/pages/AlarmDetailPage").then((m) => ({
    default: m.AlarmDetailPage,
  })),
);
const TicketsListPage = React.lazy(() =>
  import("@/pages/TicketsListPage").then((m) => ({
    default: m.TicketsListPage,
  })),
);
const FleetDevicesPage = React.lazy(() =>
  import("@/pages/FleetDevicesPage").then((m) => ({
    default: m.FleetDevicesPage,
  })),
);
const TicketDetailPage = React.lazy(() =>
  import("@/pages/TicketDetailPage").then((m) => ({
    default: m.TicketDetailPage,
  })),
);
const ReportsPage = React.lazy(() =>
  import("@/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })),
);
const OverviewPage = React.lazy(() =>
  import("@/pages/OverviewPage").then((m) => ({ default: m.OverviewPage })),
);
const MLOpsPage = React.lazy(() =>
  import("@/pages/MLOpsPage").then((m) => ({ default: m.MLOpsPage })),
);
const MLOpsSitePage = React.lazy(() =>
  import("@/pages/MLOpsSitePage").then((m) => ({ default: m.MLOpsSitePage })),
);
const MLOpsModelRedirect = React.lazy(() =>
  import("@/pages/MLOpsModelRedirect").then((m) => ({
    default: m.MLOpsModelRedirect,
  })),
);
const MLOpsRunsPage = React.lazy(() =>
  import("@/pages/MLOpsRunsPage").then((m) => ({ default: m.MLOpsRunsPage })),
);
const MLOpsRunDetailPage = React.lazy(() =>
  import("@/pages/MLOpsRunDetailPage").then((m) => ({
    default: m.MLOpsRunDetailPage,
  })),
);
const GettingStartedPage = React.lazy(() =>
  import("@/pages/GettingStartedPage").then((m) => ({
    default: m.GettingStartedPage,
  })),
);
// MV2-914 — Quote, the quoting tool as its own Produce item (SuperAdmin only,
// hidden while acting as a tenant). `/simulator/quote` and
// `/pricing-calculator` redirect here.
const QuotePage = React.lazy(() =>
  import("@/pages/QuotePage").then((m) => ({
    default: m.QuotePage,
  })),
);
// ─── Auth pages (small — eager is fine, needed before app shell loads) ────────
import { LoginPage } from "@/pages/LoginPage";
import { AcceptInvitePage } from "@/pages/AcceptInvitePage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";

// ─── Settings pages ───────────────────────────────────────────────────────────
const SettingsMyProfilePage = React.lazy(() =>
  import("@/pages/SettingsMyProfilePage").then((m) => ({
    default: m.SettingsMyProfilePage,
  })),
);
const SettingsNotificationsPage = React.lazy(() =>
  import("@/pages/SettingsNotificationsPage").then((m) => ({
    default: m.SettingsNotificationsPage,
  })),
);
const SettingsSecurityPage = React.lazy(() =>
  import("@/pages/SettingsSecurityPage").then((m) => ({
    default: m.SettingsSecurityPage,
  })),
);
const SettingsInvitationsPage = React.lazy(() =>
  import("@/pages/SettingsInvitationsPage").then((m) => ({
    default: m.SettingsInvitationsPage,
  })),
);

// ─── Admin pages — lazy + MobileGuard, tree-shaken from mobile bundle ─────────
const AdminUsersPage = React.lazy(() =>
  import("@/pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage })),
);
const AdminUserRecordPage = React.lazy(() =>
  import("@/pages/AdminUsersPage").then((m) => ({
    default: m.AdminUserRecordPage,
  })),
);
const AdminClientsPage = React.lazy(() =>
  import("@/pages/AdminClientsPage").then((m) => ({
    default: m.AdminClientsPage,
  })),
);
const AdminClientDetailPage = React.lazy(() =>
  import("@/pages/AdminClientDetailPage").then((m) => ({
    default: m.AdminClientDetailPage,
  })),
);
const AdminClientBrandingTab = React.lazy(() =>
  import("@/pages/AdminClientBrandingTab").then((m) => ({
    default: m.AdminClientBrandingTab,
  })),
);
const ClientOverviewTab = React.lazy(() =>
  import("@/pages/admin/clients/ClientOverviewTab").then((m) => ({
    default: m.ClientOverviewTab,
  })),
);
const ClientVendorAccountsTab = React.lazy(() =>
  import("@/pages/admin/clients/ClientVendorAccountsTab").then((m) => ({
    default: m.ClientVendorAccountsTab,
  })),
);
const AdminClientSettingsTab = React.lazy(() =>
  import("@/pages/AdminClientSettingsTab").then((m) => ({
    default: m.AdminClientSettingsTab,
  })),
);
const AdminCustomersPage = React.lazy(() =>
  import("@/pages/AdminCustomersPage").then((m) => ({
    default: m.AdminCustomersPage,
  })),
);
const AdminCustomerDetailPage = React.lazy(() =>
  import("@/pages/AdminCustomerDetailPage").then((m) => ({
    default: m.AdminCustomerDetailPage,
  })),
);
const CustomerOverviewTab = React.lazy(() =>
  import("@/pages/admin/customers/CustomerOverviewTab").then((m) => ({
    default: m.CustomerOverviewTab,
  })),
);
const CustomerUsersTab = React.lazy(() =>
  import("@/pages/admin/customers/CustomerUsersTab").then((m) => ({
    default: m.CustomerUsersTab,
  })),
);
const CustomerSitesTab = React.lazy(() =>
  import("@/pages/admin/customers/CustomerSitesTab").then((m) => ({
    default: m.CustomerSitesTab,
  })),
);
const AdminCustomerBrandingTab = React.lazy(() =>
  import("@/pages/AdminCustomerBrandingTab").then((m) => ({
    default: m.AdminCustomerBrandingTab,
  })),
);
const AdminCustomerSettingsTab = React.lazy(() =>
  import("@/pages/AdminCustomerSettingsTab").then((m) => ({
    default: m.AdminCustomerSettingsTab,
  })),
);
const AdminSitesPage = React.lazy(() =>
  import("@/pages/AdminSitesPage").then((m) => ({ default: m.AdminSitesPage })),
);
const AdminSiteCreatePage = React.lazy(() =>
  import("@/pages/AdminSiteCreatePage").then((m) => ({
    default: m.AdminSiteCreatePage,
  })),
);
const AdminSiteDetailPage = React.lazy(() =>
  import("@/pages/AdminSiteDetailPage").then((m) => ({
    default: m.AdminSiteDetailPage,
  })),
);
const AdminPoliciesPage = React.lazy(() =>
  import("@/pages/AdminPoliciesPage").then((m) => ({
    default: m.AdminPoliciesPage,
  })),
);
const AdminPolicyDetailPage = React.lazy(() =>
  import("@/pages/AdminPolicyDetailPage").then((m) => ({
    default: m.AdminPolicyDetailPage,
  })),
);
const AdminAuditLogPage = React.lazy(() =>
  import("@/pages/AdminAuditLogPage").then((m) => ({
    default: m.AdminAuditLogPage,
  })),
);
const AdminCrossClientPage = React.lazy(() =>
  import("@/pages/AdminCrossClientPage").then((m) => ({
    default: m.AdminCrossClientPage,
  })),
);
const AdminCrossClientDetailPage = React.lazy(() =>
  import("@/pages/admin/cross-client/AdminCrossClientDetailPage").then((m) => ({
    default: m.AdminCrossClientDetailPage,
  })),
);
const AdminCatalogsPage = React.lazy(() =>
  import("@/pages/AdminCatalogsPage").then((m) => ({
    default: m.AdminCatalogsPage,
  })),
);
const AdminProvidersTab = React.lazy(() =>
  import("@/pages/AdminCatalogsPage").then((m) => ({
    default: m.AdminProvidersTab,
  })),
);
const AdminInverterBrandsTab = React.lazy(() =>
  import("@/pages/AdminCatalogsPage").then((m) => ({
    default: m.AdminInverterBrandsTab,
  })),
);
const AdminRegisterMapsTab = React.lazy(() =>
  import("@/pages/AdminRegisterMapsTab").then((m) => ({
    default: m.AdminRegisterMapsTab,
  })),
);
const AdminEquipmentCatalogTab = React.lazy(() =>
  import("@/pages/AdminEquipmentCatalogTab").then((m) => ({
    default: m.AdminEquipmentCatalogTab,
  })),
);
const AdminVendorAccountsPage = React.lazy(() =>
  import("@/pages/AdminVendorAccountsPage").then((m) => ({
    default: m.AdminVendorAccountsPage,
  })),
);
const AdminVendorAccountDetailPage = React.lazy(() =>
  import("@/pages/AdminVendorAccountDetailPage").then((m) => ({
    default: m.AdminVendorAccountDetailPage,
  })),
);
const PlatformSettingsPage = React.lazy(() =>
  import("@/pages/PlatformSettingsPage").then((m) => ({
    default: m.PlatformSettingsPage,
  })),
);
const AdminPlatformHealthPage = React.lazy(() =>
  import("@/pages/AdminPlatformHealthPage").then((m) => ({
    default: m.AdminPlatformHealthPage,
  })),
);
const AdminPlatformEngagementPage = React.lazy(() =>
  import("@/pages/AdminPlatformEngagementPage").then((m) => ({
    default: m.AdminPlatformEngagementPage,
  })),
);

// ─── Page-level Suspense skeleton ─────────────────────────────────────────────
function PageSkeleton(): React.ReactElement {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-[color:var(--brand-primary)] border-t-transparent" />
    </div>
  );
}

/**
 * Slice 2c: /admin/branding retired — redirect per persona.
 */
function BrandingRetiredRedirect(): React.ReactElement {
  const { data } = useMe();
  if (!data) return <Navigate to="/admin/clients" replace />;
  if (data.cognito_group === "SuperAdmin") {
    return <Navigate to="/admin/clients" replace />;
  }
  return <Navigate to={`/admin/clients/${data.client.id}/branding`} replace />;
}

/** MV2-848: `/sites` is the list; `/sites?view=devices` is the fleet device timeline. */
function SitesIndex(): React.ReactElement {
  const [params] = useSearchParams();
  return params.get("view") === "devices" ? (
    <FleetDevicesPage />
  ) : (
    <SitesListPage />
  );
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/accept-invite/:token", element: <AcceptInvitePage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  {
    element: <AuthGate />,
    // Catches lazy-chunk 404s after a new deployment (Vite content-hash
    // change) and auto-reloads so the user transparently gets the new build.
    // Also surfaces a friendly "Something went wrong" UI for any other
    // unhandled route error instead of React Router's dev-only crash page.
    errorElement: <ChunkErrorElement />,
    children: [
      {
        path: "/",
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardRouter /> },
          {
            path: "inbox",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <InboxPage />
              </React.Suspense>
            ),
          },
          {
            path: "notifications",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <NotificationsPage />
              </React.Suspense>
            ),
          },
          {
            path: "overview",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <OverviewPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "map",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <MapViewPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            // MV2-692 — Site Simulator. SuperAdmin-only for now (Kranthi,
            // 10 Sep — MV2-912); the client-grantable `view_simulator`
            // feature stays on the gate for the day it reopens to
            // ClientAdmin. Matches the nav gate, and closes the direct-URL
            // bypass a nav-only gate would leave open.
            path: "simulator",
            element: (
              <RoleGate allow={["SuperAdmin"]} requireFeature="view_simulator">
                <React.Suspense fallback={<PageSkeleton />}>
                  <SimulatorPage />
                </React.Suspense>
              </RoleGate>
            ),
          },
          {
            // MV2-1100 — PVsyst import. Declared ahead of the wizard's
            // `simulator/:scenarioId` so the static segment is unambiguous
            // to a reader; React Router already ranks static above dynamic.
            path: "simulator/pvsyst",
            element: (
              <RoleGate allow={["SuperAdmin"]} requireFeature="view_simulator">
                <React.Suspense fallback={<PageSkeleton />}>
                  <PvsystImportPage />
                </React.Suspense>
              </RoleGate>
            ),
          },
          {
            // MV2-848 made the quoting tool a Simulator tab; MV2-914 gave it
            // its own Produce item at /quote.
            path: "simulator/quote",
            element: <Navigate to="/quote" replace />,
          },
          {
            // MV2-668 / MV2-914: the quoting tool — SuperAdmin-only, a
            // re:bot-internal surface hidden while acting as a tenant.
            path: "quote",
            element: (
              <RoleGate allow={["SuperAdmin"]} hideDuringImpersonation>
                <React.Suspense fallback={<PageSkeleton />}>
                  <QuotePage />
                </React.Suspense>
              </RoleGate>
            ),
          },
          {
            // MV2-913 — the wizard (`?step=`) and its Results view
            // (`/results?tab=`) share one route element so the working
            // session (the runs) survives the switch between them.
            path: "simulator/:scenarioId/:view?",
            element: (
              <RoleGate allow={["SuperAdmin"]} requireFeature="view_simulator">
                <React.Suspense fallback={<PageSkeleton />}>
                  <ScenarioEditor />
                </React.Suspense>
              </RoleGate>
            ),
          },
          {
            path: "my-plants",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <MyPlantsGridPage />
              </React.Suspense>
            ),
          },
          {
            path: "my-plant",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <MyPlantRedirect />
              </React.Suspense>
            ),
          },
          {
            path: "my-plant/:siteId",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <MyPlantPage />
              </React.Suspense>
            ),
          },
          {
            path: "sites",
            children: [
              {
                index: true,
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SitesIndex />
                  </React.Suspense>
                ),
              },
              {
                path: ":siteId",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SiteDetailShell />
                  </React.Suspense>
                ),
                children: [
                  { index: true, element: <Navigate to="overview" replace /> },
                  {
                    path: "overview",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteOverviewTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "analytics",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteAnalyticsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    // MV2-1154 — energy shell only; the tab is not on classic sites.
                    path: "assets",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteAssetsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    // MV2-1165 — one asset's page, by its model key.
                    path: "assets/:assetKey",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteAssetPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "carbon",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteCarbonTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "devices",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteDevicesTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    // MV2-856 — the device is the unit: its own page with a
                    // rail and five views (Now · History · Status · Energy · Raw).
                    path: "devices/:deviceId/:view?",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteDevicePage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "strings",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteStringsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    // MV2-954 — the verdict EirGrid would reach today. The
                    // tab is hidden on anything but an export generator
                    // (SiteDetailShell `ppmOnly`), so this route is reached
                    // only by a direct link on a non-PPM site — where the
                    // API answers `applicable: false` and the page says so.
                    path: "gridcode",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteGridCodeTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "controls",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteControlsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    // MV2-644 — the per-device config editor is a page, not a
                    // modal. Linkable, and it gets the whole viewport.
                    path: "controls/devices/:deviceId",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteControlDevicePage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "bess",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteBESSEditor />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "tariff",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteTariffTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "financials",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteFinancialsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "settings",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteSettingsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "files",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteFilesTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "alarms",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteAlarmsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "tickets",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteTicketsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "reports",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteReportsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "connectivity",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteConnectivityTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    // MV2-1099 — the MQTT message as published. SuperAdmin is
                    // enforced inside the tab (it redirects to Overview) so the
                    // 403 path matches Connectivity's rather than a bare gate.
                    path: "raw",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <SiteRawTab />
                      </React.Suspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "alarms",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <AlarmsListPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "alarms/:alarmId",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <AlarmDetailPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "tickets",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <TicketsListPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            path: "tickets/:id",
            element: (
              <SiteOwnerRedirect>
                <React.Suspense fallback={<PageSkeleton />}>
                  <TicketDetailPage />
                </React.Suspense>
              </SiteOwnerRedirect>
            ),
          },
          {
            // MV2-848: /devices folded into Sites as a view.
            path: "devices",
            element: <Navigate to="/sites?view=devices" replace />,
          },
          {
            path: "reports",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <ReportsPage />
              </React.Suspense>
            ),
          },
          // Fleet Analytics left the product (Kranthi, 25 Sep: "we don't need
          // that"); old links and bookmarks land on the Overview.
          { path: "analytics", element: <Navigate to="/overview" replace /> },
          {
            path: "ml",
            element: (
              <MobileGuard>
                <RoleGate allow={["SuperAdmin"]} hideDuringImpersonation>
                  <React.Suspense fallback={<PageSkeleton />}>
                    <MLOpsPage />
                  </React.Suspense>
                </RoleGate>
              </MobileGuard>
            ),
          },
          {
            path: "ml/sites/:piId",
            element: (
              <MobileGuard>
                <RoleGate allow={["SuperAdmin"]} hideDuringImpersonation>
                  <React.Suspense fallback={<PageSkeleton />}>
                    <MLOpsSitePage />
                  </React.Suspense>
                </RoleGate>
              </MobileGuard>
            ),
          },
          {
            // MV2-821: the model-centric page is retired; old deep links
            // resolve to the site page with the target selected.
            path: "ml/models/:modelId",
            element: (
              <MobileGuard>
                <RoleGate allow={["SuperAdmin"]} hideDuringImpersonation>
                  <React.Suspense fallback={<PageSkeleton />}>
                    <MLOpsModelRedirect />
                  </React.Suspense>
                </RoleGate>
              </MobileGuard>
            ),
          },
          {
            path: "ml/runs",
            element: (
              <MobileGuard>
                <RoleGate allow={["SuperAdmin"]} hideDuringImpersonation>
                  <React.Suspense fallback={<PageSkeleton />}>
                    <MLOpsRunsPage />
                  </React.Suspense>
                </RoleGate>
              </MobileGuard>
            ),
          },
          {
            path: "ml/runs/:runId",
            element: (
              <MobileGuard>
                <RoleGate allow={["SuperAdmin"]} hideDuringImpersonation>
                  <React.Suspense fallback={<PageSkeleton />}>
                    <MLOpsRunDetailPage />
                  </React.Suspense>
                </RoleGate>
              </MobileGuard>
            ),
          },
          // MV2-820: the Models grid and Coverage funnel folded into the
          // Health table (docs/ux/16_mlops/00_overview.md §2.1). Old links
          // keep working.
          { path: "ml/models", element: <Navigate to="/ml" replace /> },
          {
            path: "ml/coverage",
            element: <Navigate to="/ml?grade=ineligible" replace />,
          },
          {
            path: "getting-started",
            element: (
              <React.Suspense fallback={<PageSkeleton />}>
                <GettingStartedPage />
              </React.Suspense>
            ),
          },
          {
            // MV2-914: the quoting tool is Quote, its own Produce item.
            path: "pricing-calculator",
            element: <Navigate to="/quote" replace />,
          },
          {
            path: "admin",
            element: (
              <MobileGuard>
                <RoleGate allow={["SuperAdmin", "ClientAdmin"]}>
                  <AdminShell />
                </RoleGate>
              </MobileGuard>
            ),
            children: [
              { index: true, element: <AdminIndexRedirect /> },
              {
                path: "clients",
                element: (
                  <RoleGate
                    allow={["SuperAdmin"]}
                    surface="Clients"
                    surfaceReason={PLATFORM_SURFACE_REASONS.clients}
                  >
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminClientsPage />
                    </React.Suspense>
                  </RoleGate>
                ),
              },
              {
                path: "clients/:clientId",
                element: (
                  <RoleGate
                    allow={["SuperAdmin"]}
                    surface="Clients"
                    surfaceReason={PLATFORM_SURFACE_REASONS.clients}
                  >
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminClientDetailPage />
                    </React.Suspense>
                  </RoleGate>
                ),
                children: [
                  // Slice 6 (MV2-893): the record opens on its Overview hub.
                  {
                    index: true,
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <ClientOverviewTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "branding",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminClientBrandingTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "settings",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminClientSettingsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    // MV2-894: vendor accounts as a property of the client. The
                    // top-level /admin/vendor-accounts list stays until the
                    // single home is decided.
                    path: "vendor-accounts",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <ClientVendorAccountsTab />
                      </React.Suspense>
                    ),
                  },
                ],
              },
              {
                path: "customers",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminCustomersPage />
                  </React.Suspense>
                ),
              },
              {
                path: "customers/:customerId",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminCustomerDetailPage />
                  </React.Suspense>
                ),
                children: [
                  // MV2-895: Overview is the record's landing; the Slice-2c
                  // `identity` URL keeps resolving (its form lives on Overview).
                  { index: true, element: <Navigate to="overview" replace /> },
                  {
                    path: "identity",
                    element: <Navigate to="../overview" replace />,
                  },
                  {
                    path: "overview",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <CustomerOverviewTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "branding",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminCustomerBrandingTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "settings",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminCustomerSettingsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "users",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <CustomerUsersTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "sites",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <CustomerSitesTab />
                      </React.Suspense>
                    ),
                  },
                ],
              },
              {
                path: "sites",
                element: (
                  <RoleGate
                    allow={["SuperAdmin"]}
                    surface="Sites"
                    surfaceReason={PLATFORM_SURFACE_REASONS.sites}
                  >
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminSitesPage />
                    </React.Suspense>
                  </RoleGate>
                ),
              },
              {
                path: "sites/new",
                element: (
                  <RoleGate
                    allow={["SuperAdmin"]}
                    surface="Sites"
                    surfaceReason={PLATFORM_SURFACE_REASONS.sites}
                  >
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminSiteCreatePage />
                    </React.Suspense>
                  </RoleGate>
                ),
              },
              {
                // MV2-897 — the site record's tabs are in the path
                // (`/admin/sites/:id/grid#grid.mic_kw` is a readiness gap's
                // deep link); no tab redirects to `summary`.
                path: "sites/:siteId/:tab?",
                element: (
                  <RoleGate
                    allow={["SuperAdmin"]}
                    surface="Sites"
                    surfaceReason={PLATFORM_SURFACE_REASONS.sites}
                  >
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminSiteDetailPage />
                    </React.Suspense>
                  </RoleGate>
                ),
              },
              {
                path: "users",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminUsersPage />
                  </React.Suspense>
                ),
              },
              {
                // MV2-899: the routable user record (Identity · Access ·
                // Lifecycle · Audit) — same chunk as the list.
                path: "users/:userId/:tab?",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminUserRecordPage />
                  </React.Suspense>
                ),
              },
              {
                path: "policies",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminPoliciesPage />
                  </React.Suspense>
                ),
              },
              {
                path: "policies/:policyId",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminPolicyDetailPage />
                  </React.Suspense>
                ),
              },
              {
                // MV2-900: the policy record's Users tab.
                path: "policies/:policyId/users",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminPolicyDetailPage />
                  </React.Suspense>
                ),
              },
              {
                // MV2-848: invitations fold into Users as the Pending segment.
                path: "invitations",
                element: <Navigate to="/admin/users?status=pending" replace />,
              },
              {
                path: "cross-client",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminCrossClientPage />
                  </React.Suspense>
                ),
              },
              {
                // MV2-901: the cross-client record — timeline as the spine.
                path: "cross-client/:invitationId",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <AdminCrossClientDetailPage />
                  </React.Suspense>
                ),
              },
              { path: "branding", element: <BrandingRetiredRedirect /> },
              {
                path: "vendor-accounts",
                element: (
                  <RoleGate
                    allow={["SuperAdmin"]}
                    hideDuringImpersonation
                    surface="Vendor accounts"
                    surfaceReason={PLATFORM_SURFACE_REASONS.vendorAccounts}
                  >
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminVendorAccountsPage />
                    </React.Suspense>
                  </RoleGate>
                ),
              },
              {
                path: "vendor-accounts/:vendorAccountId",
                element: (
                  <RoleGate
                    allow={["SuperAdmin"]}
                    hideDuringImpersonation
                    surface="Vendor accounts"
                    surfaceReason={PLATFORM_SURFACE_REASONS.vendorAccounts}
                  >
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminVendorAccountDetailPage />
                    </React.Suspense>
                  </RoleGate>
                ),
              },
              {
                // MV2-848: Catalogs, register maps and equipment profiles become Developer.
                path: "catalogs/*",
                element: (
                  <MovedRoute from="/admin/catalogs" to="/admin/developer" />
                ),
              },
              {
                path: "developer",
                element: (
                  <RoleGate allow={["SuperAdmin"]} hideDuringImpersonation>
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminCatalogsPage />
                    </React.Suspense>
                  </RoleGate>
                ),
                children: [
                  {
                    path: "providers",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminProvidersTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "inverter-brands",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminInverterBrandsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "register-maps",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminRegisterMapsTab />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "equipment-profiles",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminEquipmentCatalogTab />
                      </React.Suspense>
                    ),
                  },
                ],
              },
              {
                // MV2-848: Platform settings + health become one tab; Health is the landing.
                path: "platform",
                element: (
                  <RoleGate allow={["SuperAdmin"]} hideDuringImpersonation>
                    <AdminPlatformShell />
                  </RoleGate>
                ),
                children: [
                  { index: true, element: <Navigate to="health" replace /> },
                  {
                    path: "health",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminPlatformHealthPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    path: "settings",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <PlatformSettingsPage />
                      </React.Suspense>
                    ),
                  },
                  {
                    // MV2-904: Engagement leaves Health's local tab for a route.
                    path: "engagement",
                    element: (
                      <React.Suspense fallback={<PageSkeleton />}>
                        <AdminPlatformEngagementPage />
                      </React.Suspense>
                    ),
                  },
                ],
              },
              {
                path: "platform-settings",
                element: <Navigate to="/admin/platform/settings" replace />,
              },
              {
                path: "platform-health",
                element: <Navigate to="/admin/platform/health" replace />,
              },
              {
                path: "providers",
                element: <Navigate to="/admin/developer/providers" replace />,
              },
              {
                path: "inverter-brands",
                element: (
                  <Navigate to="/admin/developer/inverter-brands" replace />
                ),
              },
              {
                path: "audit",
                element: (
                  <RoleGate
                    allow={["SuperAdmin"]}
                    surface="Audit"
                    surfaceReason={PLATFORM_SURFACE_REASONS.audit}
                  >
                    <React.Suspense fallback={<PageSkeleton />}>
                      <AdminAuditLogPage />
                    </React.Suspense>
                  </RoleGate>
                ),
              },
            ],
          },
          {
            path: "settings",
            element: <SettingsShell />,
            children: [
              { index: true, element: <SettingsIndex /> },
              {
                path: "my-profile",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SettingsMyProfilePage />
                  </React.Suspense>
                ),
              },
              {
                path: "notifications",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SettingsNotificationsPage />
                  </React.Suspense>
                ),
              },
              {
                path: "security",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SettingsSecurityPage />
                  </React.Suspense>
                ),
              },
              {
                path: "invitations",
                element: (
                  <React.Suspense fallback={<PageSkeleton />}>
                    <SettingsInvitationsPage />
                  </React.Suspense>
                ),
              },
              {
                path: "profile",
                element: <Navigate to="/settings/my-profile" replace />,
              },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
